import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ModalSuspenso from '../components/ModalSuspenso';
import { BRAND_NOME } from '../brand';
import styles from './Login.module.css';

export default function Login() {
  const { login, selecionarOrganizacao } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]               = useState({ email: '', senha: '' });
  const [erro, setErro]               = useState('');
  const [carregando, setCarregando]   = useState(false);
  const [etapa, setEtapa]             = useState('credenciais'); // 'credenciais' | 'organizacao'
  const [dadosSelecao, setDadosSelecao] = useState(null); // { usuario_id, organizacoes }
  const [suspenso, setSuspenso]       = useState(null); // { suporteContato } | null — Painel de Clientes
  const [limiteAtingido, setLimiteAtingido] = useState(null); // { mensagem, suporteContato } | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const resultado = await login(form.email, form.senha);
      if (resultado.requer_selecao) {
        setDadosSelecao({ usuario_id: resultado.usuario_id, organizacoes: resultado.organizacoes });
        setEtapa('organizacao');
      } else {
        navegarPosLogin(resultado);
      }
    } catch (err) {
      // codigo estruturado (não só a mensagem) — distingue "conta suspensa"
      // (Painel de Clientes, organizacoes.ativo=false) de senha errada, pra
      // abrir o modal dedicado em vez do erro genérico do formulário.
      const codigo = err.response?.data?.codigo;
      if (codigo === 'ORG_SUSPENSA') {
        setSuspenso({ suporteContato: err.response.data.suporte_contato || null });
      } else if (codigo === 'LIMITE_SIMULTANEOS') {
        setLimiteAtingido({ mensagem: err.response.data.erro, suporteContato: err.response.data.suporte_contato || null });
      } else {
        setErro(err.response?.data?.erro || 'E-mail/usuário ou senha inválidos.');
      }
    } finally {
      setCarregando(false);
    }
  };

  // Usuário marcado como Garçom (Administrativo → Usuários) já cai direto em
  // Mesas/Comandas — no desktop abre o PDV com o F1 já aberto; pelo celular a
  // GuardaMobile (App.jsx) intercepta essa mesma rota e manda pra tela mobile.
  // Fila de Produção (tela da cozinha) e Comanda (Mesas/Comandas mobile,
  // pronto pra lançar pedido, sem passar pelo PDV) são checagens à parte,
  // mutuamente exclusivas entre si e com Garçom — ver CadastroUsuarios.jsx.
  const navegarPosLogin = (usuarioLogado) => {
    if (usuarioLogado?.fila_producao) navigate('/fila-producao');
    else if (usuarioLogado?.comanda) navigate('/mesas-mobile');
    else if (usuarioLogado?.garcom) navigate('/vendas/pdv', { state: { abrirMesas: true } });
    else navigate('/dashboard');
  };

  const handleSelecionarOrg = async (orgId) => {
    setErro('');
    setCarregando(true);
    try {
      const usuarioLogado = await selecionarOrganizacao(dadosSelecao.usuario_id, orgId);
      navegarPosLogin(usuarioLogado);
    } catch (err) {
      if (err.response?.data?.codigo === 'LIMITE_SIMULTANEOS') {
        setLimiteAtingido({ mensagem: err.response.data.erro, suporteContato: err.response.data.suporte_contato || null });
      } else {
        setErro('Erro ao selecionar organização.');
      }
    } finally {
      setCarregando(false);
    }
  };

  if (etapa === 'organizacao') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.titulo}>Selecionar Empresa</h1>
          <p className={styles.subtitulo}>Escolha com qual organização deseja trabalhar</p>

          {erro && <div className={styles.erro}>{erro}</div>}

          <div className={styles.orgLista}>
            {dadosSelecao.organizacoes.map(org => (
              <button
                key={org.id}
                className={`${styles.orgCard} ${org.padrao ? styles.orgCardPadrao : ''}`}
                onClick={() => handleSelecionarOrg(org.id)}
                disabled={carregando}
              >
                <span className={styles.orgNome}>{org.razao_social}</span>
                {org.nome_fantasia && org.nome_fantasia !== org.razao_social && (
                  <span className={styles.orgFantasia}>{org.nome_fantasia}</span>
                )}
                {org.cnpj && <span className={styles.orgCnpj}>{org.cnpj}</span>}
                {org.padrao && <span className={styles.orgPadraoBadge}>Padrão</span>}
              </button>
            ))}
          </div>

          <button
            className={styles.btnVoltar}
            onClick={() => { setEtapa('credenciais'); setErro(''); }}
            disabled={carregando}
          >
            ← Trocar conta
          </button>
        </div>

        {limiteAtingido && (
          <ModalSuspenso
            emoji="🚦"
            titulo="LIMITE DE ACESSOS ATINGIDO"
            mensagem={limiteAtingido.mensagem}
            suporteContato={limiteAtingido.suporteContato}
            onFechar={() => setLimiteAtingido(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <div className={styles.logoArea}>
          <span className={styles.logoBadge}>E+</span>
          <span className={styles.logoNome}>{BRAND_NOME}</span>
        </div>
        <h1 className={styles.titulo}>Acesse sua conta</h1>
        <p className={styles.subtitulo}>Sistema de Gestão Empresarial · SaaS</p>

        {erro && <div className={styles.erro}>{erro}</div>}

        <label className={styles.label}>
          E-mail ou usuário
          <input
            type="text"
            className={styles.input}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            autoFocus
            autoComplete="username"
          />
        </label>

        <label className={styles.label}>
          Senha
          <input
            type="password"
            className={styles.input}
            value={form.senha}
            onChange={(e) => setForm({ ...form, senha: e.target.value })}
            required
          />
        </label>

        <button className={styles.btn} type="submit" disabled={carregando}>
          {carregando ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      {suspenso && (
        <ModalSuspenso suporteContato={suspenso.suporteContato} onFechar={() => setSuspenso(null)} />
      )}
      {limiteAtingido && (
        <ModalSuspenso
          emoji="🚦"
          titulo="LIMITE DE ACESSOS ATINGIDO"
          mensagem={limiteAtingido.mensagem}
          suporteContato={limiteAtingido.suporteContato}
          onFechar={() => setLimiteAtingido(null)}
        />
      )}
    </div>
  );
}
